import { FloatingPortal, useMergeRefs } from '@floating-ui/react';
import { forwardRef } from 'react';
import clsx from 'clsx';
import { DropdownPlacement } from '../../../types';
import { useTooltipContext } from './TooltipContext';

const TOP_TICK_CLASS =
  'after:top-full after:-translate-x-1/2 after:left-1/2 after:border-t-base-100 after:border-x-transparent after:border-b-transparent';
const BOTTOM_TICK_CLASS =
  'after:bottom-full after:-translate-x-1/2 after:left-1/2 after:border-b-base-100 after:border-x-transparent after:border-t-transparent';
const LEFT_TICK_CLASS =
  'after:left-full after:-translate-y-1/2 after:top-1/2 after:border-l-base-100 after:border-y-transparent after:border-r-transparent';
const RIGHT_TICK_CLASS =
  'after:right-full after:-translate-y-1/2 after:top-1/2 after:border-r-base-100 after:border-y-transparent after:border-l-transparent';

const PLACEMENT_TO_TICK_CLASS = {
  [DropdownPlacement.TOP]: TOP_TICK_CLASS,
  [DropdownPlacement.TOP_START]: TOP_TICK_CLASS,
  [DropdownPlacement.TOP_END]: TOP_TICK_CLASS,
  [DropdownPlacement.BOTTOM]: BOTTOM_TICK_CLASS,
  [DropdownPlacement.BOTTOM_START]: BOTTOM_TICK_CLASS,
  [DropdownPlacement.BOTTOM_END]: BOTTOM_TICK_CLASS,
  [DropdownPlacement.LEFT]: LEFT_TICK_CLASS,
  [DropdownPlacement.LEFT_START]: LEFT_TICK_CLASS,
  [DropdownPlacement.LEFT_END]: LEFT_TICK_CLASS,
  [DropdownPlacement.RIGHT]: RIGHT_TICK_CLASS,
  [DropdownPlacement.RIGHT_START]: RIGHT_TICK_CLASS,
  [DropdownPlacement.RIGHT_END]: RIGHT_TICK_CLASS,
};

/**
 * TooltipContent component that renders the content of the tooltip.
 * It uses a portal to render the tooltip in a different part of the DOM.
 */
const TooltipContent = forwardRef<HTMLDivElement, React.HTMLProps<HTMLDivElement>>(function TooltipContent(
  { style, ...props },
  propRef
) {
  const context = useTooltipContext();
  const ref = useMergeRefs([context.refs.setFloating, propRef]);

  // Do not render if tooltip is not open or no children are provided.
  if (!context.open || !props.children) return null;

  const placement = context.placement || DropdownPlacement.TOP;

  return (
    <FloatingPortal>
      <div
        className={clsx(
          'z-[9999] max-w-40 rounded-lg bg-base-100 px-2 py-1 text-xs text-base-content/60 shadow-dialog after:pointer-events-none after:absolute after:border-[4px] after:border-solid',
          PLACEMENT_TO_TICK_CLASS[placement]
        )}
        ref={ref}
        style={{
          ...context.floatingStyles,
          ...style,
        }}
        {...context.getFloatingProps(props)}
      />
    </FloatingPortal>
  );
});

export default TooltipContent;
